export const queryKeys = {
  projects: {
    all: ['projects'] as const,
    detail: (id: string) => ['projects', id] as const,
    campaigns: (projectId: string) => ['projects', projectId, 'campaigns'] as const,
  },
  brands: {
    all: ['projects'] as const,
    detail: (id: string) => ['projects', id] as const,
    campaigns: (brandId: string) => ['projects', brandId, 'campaigns'] as const,
  },
  campaigns: {
    all: ['campaigns'] as const,
    detail: (id: string) => ['campaigns', id] as const,
    concepts: (campaignId: string) => ['campaigns', campaignId, 'routes'] as const,
    approvalStatus: (campaignId: string) => ['campaigns', campaignId, 'approval-status'] as const,
  },
  blueprints: {
    all: ['blueprints'] as const,
    byOrg: (orgId: string) => ['blueprints', orgId] as const,
  },
  narrativePacks: {
    versions: (projectId: string) => ['narrative-packs', projectId, 'versions'] as const,
  },
  generation: {
    run: (runId: string) => ['generation-runs', runId] as const,
    byCampaign: (campaignId: string) => ['generation-runs', 'campaign', campaignId] as const,
    variants: (runId: string) => ['generation-runs', runId, 'variants'] as const,
  },
  review: {
    decisions: (campaignId: string) => ['review-decisions', campaignId] as const,
  },
  publish: {
    package: (packageId: string) => ['publish-packages', packageId] as const,
    byCampaign: (campaignId: string) => ['publish-packages', 'campaign', campaignId] as const,
  },
  chat: {
    threads: (projectId: string) => ['chat-threads', projectId] as const,
    messages: (threadId: string) => ['chat-messages', threadId] as const,
  },
  contextSources: {
    byProject: (projectId: string) => ['context-sources', projectId] as const,
    detail: (sourceId: string) => ['context-sources', 'detail', sourceId] as const,
  },
  referenceAssets: {
    byProject: (projectId: string) => ['reference-assets', projectId] as const,
    detail: (assetId: string) => ['reference-assets', 'detail', assetId] as const,
  },
  organizations: {
    detail: (id: string) => ['organizations', id] as const,
    members: (id: string) => ['organizations', id, 'members'] as const,
    invitations: (id: string) => ['organizations', id, 'invitations'] as const,
    usage: (orgId: string) => ['organizations', orgId, 'usage'] as const,
  },
  activities: {
    campaign: (campaignId: string) => ['activities', 'campaign', campaignId] as const,
  },
  notifications: {
    list: (userId: string) => ['notifications', userId] as const,
    unreadCount: (userId: string) => ['notifications', 'unread', userId] as const,
  },
  comments: {
    campaign: (campaignId: string) => ['comments', 'campaign', campaignId] as const,
  },
  analytics: {
    campaign: (campaignId: string) => ['analytics', 'campaign', campaignId] as const,
    campaignOutcomes: (campaignId: string) =>
      ['analytics', 'campaign', campaignId, 'outcomes'] as const,
    projectOutcomes: (projectId: string) =>
      ['analytics', 'project', projectId, 'outcomes'] as const,
    projectReports: (projectId: string, type?: 'daily' | 'weekly', limit?: number) =>
      ['analytics', 'project', projectId, 'reports', type, limit] as const,
    organization: (orgId: string) => ['analytics', 'organization', orgId] as const,
  },
  evidence: {
    byProject: (projectId: string) => ['evidence', projectId] as const,
  },
  competitors: {
    byProject: (projectId: string) => ['competitors', projectId] as const,
    detail: (id: string) => ['competitors', 'detail', id] as const,
  },
  assetLibrary: {
    byProject: (projectId: string) => ['asset-library', projectId] as const,
    stats: (projectId: string) => ['asset-library', 'stats', projectId] as const,
    detail: (assetId: string) => ['asset-library', 'detail', assetId] as const,
  },
  schedule: {
    byProject: (projectId: string) => ['schedules', projectId] as const,
    byCampaign: (campaignId: string) => ['schedules', 'campaign', campaignId] as const,
  },
  social: {
    accounts: (projectId: string) => ['social', projectId, 'accounts'] as const,
    health: (projectId: string) => ['social', projectId, 'health'] as const,
  },
  aiUsage: {
    providerHealth: (orgId: string) => ['ai-usage', 'provider-health', orgId] as const,
    costBreakdown: (orgId: string) => ['ai-usage', 'cost-breakdown', orgId] as const,
    recentFailures: (orgId: string) => ['ai-usage', 'recent-failures', orgId] as const,
    dailyUsage: (orgId: string) => ['ai-usage', 'daily-usage', orgId] as const,
  },
  billing: {
    plans: ['billing', 'plans'] as const,
    status: (orgId: string) => ['billing', 'status', orgId] as const,
    paypalConfig: ['billing', 'paypal-config'] as const,
  },
  admin: {
    overview: ['admin', 'overview'] as const,
    organizations: (search?: string, page?: number) =>
      ['admin', 'organizations', search, page] as const,
    organizationDetail: (id: string) => ['admin', 'organizations', id] as const,
    users: (search?: string, page?: number) => ['admin', 'users', search, page] as const,
    plans: ['admin', 'plans'] as const,
    modelPricing: ['admin', 'model-pricing'] as const,
    aiUsageOverview: (days?: number) => ['admin', 'ai-usage', 'overview', days] as const,
    aiUsageByModel: (days?: number) => ['admin', 'ai-usage', 'by-model', days] as const,
    aiUsageByOrg: (days?: number) => ['admin', 'ai-usage', 'by-org', days] as const,
    aiUsageDaily: (days?: number) => ['admin', 'ai-usage', 'daily', days] as const,
    aiUsageByFunction: (days?: number) => ['admin', 'ai-usage', 'by-function', days] as const,
    aiUsageByProject: (days?: number) => ['admin', 'ai-usage', 'by-project', days] as const,
    aiUsageTopCalls: (days?: number, limit?: number) =>
      ['admin', 'ai-usage', 'top-calls', days, limit] as const,
    providerHealth: (windowMinutes?: number) =>
      ['admin', 'ai-usage', 'provider-health', windowMinutes] as const,
    paymentGateway: ['admin', 'payment-gateway'] as const,
    paypalStatus: ['admin', 'paypal', 'status'] as const,
    paypalProducts: ['admin', 'paypal', 'products'] as const,
    paypalPlans: (productId?: string) => ['admin', 'paypal', 'plans', productId] as const,
    paypalPlanDetail: (id: string) => ['admin', 'paypal', 'plans', 'detail', id] as const,
    paypalWebhooks: ['admin', 'paypal', 'webhooks'] as const,
  },
};
